import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Badge,
  Button,
  Drawer,
  Empty,
  Flex,
  Input,
  List,
  Spin,
  Tabs,
  Tooltip,
  Typography,
  theme,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComments,
  faPaperPlane,
  faArrowLeft,
  faBell,
  faCheckDouble,
  faTrash,
  faDumbbell,
  faFolderOpen,
  faVideo,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import "dayjs/locale/it";
import relativeTime from "dayjs/plugin/relativeTime";
import { useAuth } from "../../contexts/auth";
import { useRealtime, RealtimeMessage } from "../../contexts/realtime";
import { Chat, ChatMessage, Conversation } from "../../api/Chat";
import { Mentoring, MentoringPartner } from "../../api/Mentoring";
import { AppNotification, NotificationType } from "../../api/Notifications";
import "./ChatWidget.css";

dayjs.extend(relativeTime);
dayjs.locale("it");

const { Text } = Typography;

interface PartnerRow {
  partner: MentoringPartner;
  conversation?: Conversation;
  lastMessage?: ChatMessage;
  unread: number;
}

const initials = (nome?: string, cognome?: string) =>
  `${(nome?.[0] || "").toUpperCase()}${(cognome?.[0] || "").toUpperCase()}` || "?";

const notifIcon = (type: NotificationType) => {
  switch (type) {
    case "programmazione":
      return faDumbbell;
    case "mentoring":
      return faFolderOpen;
    case "video":
      return faVideo;
    default:
      return faCircleInfo;
  }
};

export default function ChatWidget() {
  const { loggedUser } = useAuth();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const {
    chatUnread,
    notifUnread,
    notifications,
    refreshNotifications,
    refreshCounts,
    markNotificationRead,
    markAllNotificationsRead,
    removeNotification,
    subscribe,
  } = useRealtime();

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("chat");
  const [loadingList, setLoadingList] = useState(false);
  const [rows, setRows] = useState<PartnerRow[]>([]);
  const [activePartner, setActivePartner] = useState<PartnerRow | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const loadListInFlight = useRef(false);

  const myId = loggedUser?.id;
  const totalUnread = chatUnread + notifUnread;

  const otherIdOf = useCallback(
    (conv: Conversation) =>
      conv.participant1Id === myId ? conv.participant2Id : conv.participant1Id,
    [myId]
  );

  // Load the partner list + conversations, then per-conversation messages to
  // derive unread/last-message. Partner count is small (a trainer's athletes /
  // an athlete's single trainer) so the fan-out is cheap.
  const loadList = useCallback(async () => {
    if (!myId || loadListInFlight.current) return;
    loadListInFlight.current = true;
    setLoadingList(true);
    try {
      const [partnersRes, convRes] = await Promise.all([
        Mentoring.getPartners(),
        Chat.getMyConversations(myId),
      ]);
      const partners = partnersRes.data.list ?? [];
      const conversations = convRes.data.list ?? [];
      const convByOther = new Map<number, Conversation>();
      conversations.forEach((c) => convByOther.set(otherIdOf(c), c));

      const built: PartnerRow[] = await Promise.all(
        partners.map(async (partner) => {
          const conversation = convByOther.get(partner.id);
          if (!conversation) return { partner, unread: 0 };
          try {
            const msgs = (await Chat.getMessages(conversation.id)).data.list ?? [];
            const unread = msgs.filter((m) => m.senderId !== myId && !m.read).length;
            return {
              partner,
              conversation,
              lastMessage: msgs[msgs.length - 1],
              unread,
            };
          } catch (_) {
            return { partner, conversation, unread: 0 };
          }
        })
      );

      built.sort((a, b) => {
        if (a.unread !== b.unread) return b.unread - a.unread;
        const at = a.lastMessage?.createdAt ? dayjs(a.lastMessage.createdAt).valueOf() : 0;
        const bt = b.lastMessage?.createdAt ? dayjs(b.lastMessage.createdAt).valueOf() : 0;
        return bt - at;
      });
      setRows(built);
    } finally {
      setLoadingList(false);
      loadListInFlight.current = false;
    }
  }, [myId, otherIdOf]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, []);

  const openThread = useCallback(
    async (row: PartnerRow) => {
      if (!myId) return;
      setActivePartner(row);
      setLoadingThread(true);
      setMessages([]);
      try {
        let conversation = row.conversation;
        if (!conversation) {
          // Find-or-create on the BE.
          conversation = (
            await Chat.createConversation(myId, row.partner.id)
          ).data.conversation;
          setActivePartner({ ...row, conversation });
        }
        const msgs = (await Chat.getMessages(conversation.id)).data.list ?? [];
        setMessages(msgs);
        // Mark the whole conversation read.
        if (msgs.some((m) => m.senderId !== myId && !m.read)) {
          await Chat.markConversationRead(conversation.id);
          refreshCounts();
        }
        scrollToBottom();
      } finally {
        setLoadingThread(false);
      }
    },
    [myId, refreshCounts, scrollToBottom]
  );

  const send = useCallback(async () => {
    const text = draft.trim();
    const conversation = activePartner?.conversation;
    if (!text || !conversation || !myId || sending) return;
    setSending(true);
    try {
      await Chat.sendMessage(conversation.id, myId, text);
      setDraft("");
      const msgs = (await Chat.getMessages(conversation.id)).data.list ?? [];
      setMessages(msgs);
      scrollToBottom();
    } finally {
      setSending(false);
    }
  }, [draft, activePartner, myId, sending, scrollToBottom]);

  // Live updates: when a message lands in the open conversation, reload it;
  // otherwise refresh the partner list badges.
  useEffect(() => {
    const unsub = subscribe((msg: RealtimeMessage) => {
      if (msg.type === "new_chat") {
        const list: any[] = Array.isArray(msg.data) ? msg.data : [msg.data];
        const convId = list[0]?.conversationId ?? list[0]?.dataValues?.conversationId;
        if (activePartner?.conversation && convId === activePartner.conversation.id) {
          Chat.getMessages(activePartner.conversation.id)
            .then((res) => {
              const msgs = res.data.list ?? [];
              setMessages(msgs);
              scrollToBottom();
              // Only mark read when something is actually unread — otherwise the
              // mark-read → (potential) echo would re-trigger this handler in a
              // loop. After the first mark, messages are all read → no re-mark.
              const hasUnread = msgs.some((m) => m.senderId !== myId && !m.read);
              if (myId && hasUnread) {
                Chat.markConversationRead(activePartner.conversation!.id).then(refreshCounts);
              }
            })
            .catch(() => {});
        } else if (open && activeTab === "chat") {
          loadList();
        }
      }
    });
    return unsub;
  }, [subscribe, activePartner, open, activeTab, loadList, myId, refreshCounts, scrollToBottom]);

  // When the drawer opens on the chat tab, (re)load the list.
  useEffect(() => {
    if (open && activeTab === "chat") loadList();
    if (open && activeTab === "notifiche") refreshNotifications();
  }, [open, activeTab, loadList, refreshNotifications]);

  const handleNotificationClick = useCallback(
    (n: AppNotification) => {
      if (!n.read) markNotificationRead(n.id);
      if (n.type === "programmazione" && n.relatedId) {
        navigate(`/programmazioni/${n.relatedId}`);
        setOpen(false);
      } else if (n.type === "message") {
        setActiveTab("chat");
      } else if (n.type === "mentoring") {
        navigate("/mentoring");
        setOpen(false);
      } else if (n.type === "video") {
        navigate("/video-tutorial");
        setOpen(false);
      }
    },
    [markNotificationRead, navigate]
  );

  const chatList = useMemo(
    () => (
      <List
        loading={loadingList}
        dataSource={rows}
        locale={{
          emptyText: (
            <Empty description="Nessun contatto disponibile" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ),
        }}
        renderItem={(row) => (
          <List.Item
            className="cw-partner-row"
            onClick={() => openThread(row)}
            style={{ cursor: "pointer", padding: "10px 12px" }}
          >
            <List.Item.Meta
              avatar={
                <Badge count={row.unread} size="small" offset={[-2, 4]}>
                  <Avatar style={{ backgroundColor: token.colorPrimary }}>
                    {initials(row.partner.nome, row.partner.cognome)}
                  </Avatar>
                </Badge>
              }
              title={
                <Flex justify="space-between" align="center">
                  <span>
                    {row.partner.nome} {row.partner.cognome}
                  </span>
                  {row.lastMessage?.createdAt && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {dayjs(row.lastMessage.createdAt).fromNow(true)}
                    </Text>
                  )}
                </Flex>
              }
              description={
                <Text type="secondary" ellipsis style={{ fontSize: 12 }}>
                  {row.lastMessage
                    ? `${row.lastMessage.senderId === myId ? "Tu: " : ""}${row.lastMessage.text}`
                    : "Inizia la conversazione"}
                </Text>
              }
            />
          </List.Item>
        )}
      />
    ),
    [rows, loadingList, openThread, token.colorPrimary, myId]
  );

  const thread = activePartner && (
    <Flex vertical style={{ height: "100%" }}>
      <Flex align="center" gap={8} className="cw-thread-header">
        <Button
          type="text"
          icon={<FontAwesomeIcon icon={faArrowLeft} />}
          onClick={() => {
            setActivePartner(null);
            loadList();
          }}
        />
        <Avatar style={{ backgroundColor: token.colorPrimary }}>
          {initials(activePartner.partner.nome, activePartner.partner.cognome)}
        </Avatar>
        <strong>
          {activePartner.partner.nome} {activePartner.partner.cognome}
        </strong>
      </Flex>

      <div className="cw-messages">
        {loadingThread ? (
          <Flex justify="center" align="center" style={{ height: "100%" }}>
            <Spin />
          </Flex>
        ) : messages.length === 0 ? (
          <Empty description="Nessun messaggio" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          messages.map((m) => {
            const mine = m.senderId === myId;
            return (
              <div key={m.id} className={`cw-bubble-row ${mine ? "mine" : "theirs"}`}>
                <div
                  className="cw-bubble"
                  style={{
                    background: mine ? token.colorPrimary : token.colorFillSecondary,
                    color: mine ? "#fff" : token.colorText,
                  }}
                >
                  <div>{m.text}</div>
                  <div className="cw-bubble-time">
                    {m.createdAt ? dayjs(m.createdAt).format("HH:mm") : ""}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      <Flex gap={8} className="cw-composer">
        <Input.TextArea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Scrivi un messaggio…"
          autoSize={{ minRows: 1, maxRows: 4 }}
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        <Button
          type="primary"
          loading={sending}
          onClick={send}
          icon={<FontAwesomeIcon icon={faPaperPlane} />}
        />
      </Flex>
    </Flex>
  );

  const notificationsList = (
    <Flex vertical style={{ height: "100%" }}>
      {notifications.length > 0 && (
        <Flex justify="flex-end" style={{ padding: "4px 8px" }}>
          <Button
            size="small"
            type="link"
            icon={<FontAwesomeIcon icon={faCheckDouble} />}
            onClick={markAllNotificationsRead}
          >
            Segna tutte come lette
          </Button>
        </Flex>
      )}
      <div style={{ flex: 1, overflowY: "auto" }}>
        <List
          dataSource={notifications}
          locale={{
            emptyText: (
              <Empty description="Nessuna notifica" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ),
          }}
          renderItem={(n) => (
            <List.Item
              className={`cw-notif-row ${n.read ? "" : "unread"}`}
              onClick={() => handleNotificationClick(n)}
              style={{ cursor: "pointer", padding: "10px 12px" }}
              actions={[
                <Tooltip title="Elimina" key="del">
                  <Button
                    type="text"
                    size="small"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeNotification(n.id);
                    }}
                  />
                </Tooltip>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Badge dot={!n.read} offset={[-2, 4]}>
                    <Avatar
                      style={{
                        backgroundColor: n.read ? token.colorFillSecondary : token.colorPrimary,
                        color: n.read ? token.colorTextSecondary : "#fff",
                      }}
                      icon={<FontAwesomeIcon icon={notifIcon(n.type)} />}
                    />
                  </Badge>
                }
                title={<span style={{ fontWeight: n.read ? 400 : 600 }}>{n.title}</span>}
                description={
                  <Flex vertical>
                    {n.body && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {n.body}
                      </Text>
                    )}
                    {n.createdAt && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {dayjs(n.createdAt).fromNow()}
                      </Text>
                    )}
                  </Flex>
                }
              />
            </List.Item>
          )}
        />
      </div>
    </Flex>
  );

  // Hidden until a user is logged in.
  if (!loggedUser?.id) return null;

  return (
    <>
      <Badge
        count={totalUnread}
        overflowCount={99}
        offset={[-8, 6]}
        className="cw-fab-badge"
        title={totalUnread ? `${totalUnread} non lette` : undefined}
      >
        <button
          type="button"
          aria-label={
            totalUnread
              ? `Apri chat e notifiche, ${totalUnread} non lette`
              : "Apri chat e notifiche"
          }
          className="cw-fab"
          style={{ background: token.colorPrimary }}
          onClick={() => setOpen(true)}
        >
          <FontAwesomeIcon icon={faComments} style={{ color: "#fff", fontSize: 24 }} />
        </button>
      </Badge>

      <Drawer
        title={
          <Flex align="center" gap={8}>
            <FontAwesomeIcon icon={faComments} />
            <span>Messaggi & Notifiche</span>
          </Flex>
        }
        placement="right"
        open={open}
        onClose={() => {
          setOpen(false);
          setActivePartner(null);
        }}
        width={400}
        styles={{ body: { padding: 0, height: "100%" } }}
        className="cw-drawer"
      >
        {activePartner ? (
          <div style={{ height: "100%" }}>{thread}</div>
        ) : (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            centered
            style={{ height: "100%" }}
            className="cw-tabs"
            items={[
              {
                key: "chat",
                label: (
                  <Badge count={chatUnread} size="small" offset={[8, -2]}>
                    <span>
                      <FontAwesomeIcon icon={faComments} className="me-2" />
                      Chat
                    </span>
                  </Badge>
                ),
                children: <div className="cw-tab-body">{chatList}</div>,
              },
              {
                key: "notifiche",
                label: (
                  <Badge count={notifUnread} size="small" offset={[8, -2]}>
                    <span>
                      <FontAwesomeIcon icon={faBell} className="me-2" />
                      Notifiche
                    </span>
                  </Badge>
                ),
                children: <div className="cw-tab-body">{notificationsList}</div>,
              },
            ]}
          />
        )}
      </Drawer>
    </>
  );
}
